import { useCallback, useMemo, useState } from 'react';
import { type OpenApiSchemaDashboardTodolistItem } from '#shared/api/schema';
import { type AutomaticPresentationEligibility } from '#shared/lib/automaticPresentation';
import { withinLastMs } from '#shared/lib/datetime';
import { DASHBOARD_TODO_LIST_FEATURE_HINT_IDS, type DashboardTodoListFeatureHintId } from '#shared/lib/featureHints';
import { DASHBOARD_TODO_LIST_HINT_COPY, DASHBOARD_TODO_LIST_MAX_COMPLETED_ITEMS } from '../constants';
import { useDashboardTodoListFeatureHints } from '../useDashboardTodoListFeatureHints';

export type DashboardTodoListContentModelInput = {
    items: OpenApiSchemaDashboardTodolistItem[];
    onToggleItemComplete: (item: OpenApiSchemaDashboardTodolistItem) => void;
};

export function useDashboardTodoListContentModel({ items, onToggleItemComplete }: DashboardTodoListContentModelInput) {
    const [openItemId, setOpenItemId] = useState<string | null>(items[0]?.id ?? null);

    const makeExpandToggle = useCallback(
        (itemId: string) => (nextExpanded: boolean) => {
            setOpenItemId(nextExpanded ? itemId : null);
        },
        []
    );

    const handleToggleItemCompleteAndCollapseOpen = useCallback(
        (item: OpenApiSchemaDashboardTodolistItem) => {
            setOpenItemId(null);
            onToggleItemComplete(item);
        },
        [onToggleItemComplete]
    );

    const itemsToComplete = useMemo(() => items.filter((item) => !item.completed_at), [items]);

    const firstIncomplete = itemsToComplete[0];

    const eligibility = useMemo((): Record<DashboardTodoListFeatureHintId, AutomaticPresentationEligibility> => {
        return {
            [DASHBOARD_TODO_LIST_FEATURE_HINT_IDS.TITLE]: firstIncomplete ? 'eligible' : 'ineligible',
            [DASHBOARD_TODO_LIST_FEATURE_HINT_IDS.COMPLETION_ICON]: firstIncomplete ? 'eligible' : 'ineligible',
            [DASHBOARD_TODO_LIST_FEATURE_HINT_IDS.NEW_LABEL]:
                firstIncomplete && withinLastMs(firstIncomplete.created_at) ? 'eligible' : 'ineligible',
        };
    }, [firstIncomplete]);

    const { activeHintId, closingHintId, dismiss, completeClosing } = useDashboardTodoListFeatureHints(eligibility);

    const firstIncompleteFeatureHints = useMemo(() => {
        if (!firstIncomplete) {
            return undefined;
        }

        return {
            activeHintId,
            closingHintId,
            copy: DASHBOARD_TODO_LIST_HINT_COPY,
            onDismiss: dismiss,
            onCloseComplete: completeClosing,
        };
    }, [activeHintId, closingHintId, completeClosing, dismiss, firstIncomplete]);

    const itemsCompleted = useMemo(
        () =>
            items
                .filter((item) => Boolean(item.completed_at))
                .sort((a, b) => new Date(b.completed_at ?? '').getTime() - new Date(a.completed_at ?? '').getTime())
                .slice(0, DASHBOARD_TODO_LIST_MAX_COMPLETED_ITEMS),
        [items]
    );

    return {
        openItemId,
        makeExpandToggle,
        handleToggleItemCompleteAndCollapseOpen,
        itemsToComplete,
        itemsCompleted,
        firstIncomplete,
        firstIncompleteFeatureHints,
    };
}
